import React, { useState, useEffect } from "react";
import "./TaskDetailModal.css";

const TaskDetailModal = ({ task, onClose, onUpdate, onDelete, userRole }) => {
  const [comments, setComments] = useState(task.comments || []);
  const [newComment, setNewComment] = useState("");
  const [progress, setProgress] = useState(task.progress || 0);
  const [updating, setUpdating] = useState(false);
  const [addingComment, setAddingComment] = useState(false);

  // New states for updates
  const [showDailyUpdate, setShowDailyUpdate] = useState(false);
  const [showWeeklyUpdate, setShowWeeklyUpdate] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [taskDetails, setTaskDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Daily update form state
  const [dailyUpdate, setDailyUpdate] = useState({
    workDone: "",
    timeSpent: "",
    challenges: "",
    nextDayPlan: "",
  });

  // Weekly update form state
  const [weeklyUpdate, setWeeklyUpdate] = useState({
    weeklyProgress: "",
    hoursWorked: "",
    achievements: "",
    blockers: "",
    overallRating: 3,
  });

  // Completion form state
  const [completion, setCompletion] = useState({
    completionNotes: "",
    finalReport: "",
    deliverables: [{ name: "", description: "" }],
    qualityRating: 3,
  });

  const isAssignedToMe = task.assignedTo._id === localStorage.getItem("userId");
  const canUpdateStatus =
    isAssignedToMe || userRole === "admin" || userRole === "hr";
  const canComment = true;
  const canAddUpdates = isAssignedToMe && task.status !== "completed";
  const canViewDetails =
    userRole === "admin" ||
    userRole === "hr" ||
    task.assignedBy._id === localStorage.getItem("userId");

  // Check if user can delete the task (admin, HR, supervisor, or creator)
  const createdById = task.createdBy?._id;
  const currentUserId = localStorage.getItem("userId");
  const canDelete =
    ["admin", "hr", "supervisor"].includes(userRole) ||
    createdById === currentUserId;

  useEffect(() => {
    setComments(task.comments || []);
    setProgress(task.progress || 0);

    // Load task details if admin/hr
    if (canViewDetails) {
      loadTaskDetails();
    }
  }, [task, canViewDetails]);

  const loadTaskDetails = async () => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem("token"); // ✅ FIXED
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/daily-update`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setTaskDetails(data);
      }
    } catch (error) {
      console.error("Error loading task details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!canUpdateStatus) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem("token"); // ✅ FIXED
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            progress: newStatus === "completed" ? 100 : progress,
            completedAt: newStatus === "completed" ? new Date() : null,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        onUpdate(data.task);
        loadTaskDetails();
      } else {
        console.error("Error updating task:", data.message);
      }
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = () => {
    if (
      window.confirm(
        `Are you sure you want to delete this task?\n\n"${task.title}"\n\nThis action cannot be undone. All task data, updates, and comments will be permanently removed.`
      )
    ) {
      handleConfirmDelete();
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!canDelete || !task?._id) return;

    try {
      const token = localStorage.getItem("token"); // ✅ FIXED
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        onClose();
        onDelete && onDelete(task._id);
      } else {
        alert(data.message || "Error deleting task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("Error deleting task");
    }
  };

  const handleDailyUpdate = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("token"); // ✅ FIXED

      // ✅ Add token validation
      if (!token) {
        alert("Please login again");
        window.location.href = "/login";
        return;
      }

      console.log("Token exists:", !!token); // Debug log
      console.log("Daily update data:", dailyUpdate); // Debug log

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/daily-update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(dailyUpdate),
        }
      );

      console.log("Response status:", response.status); // Debug log

      const data = await response.json();
      console.log("Response data:", data); // Debug log

      if (data.success) {
        setShowDailyUpdate(false);
        setDailyUpdate({
          workDone: "",
          timeSpent: "",
          challenges: "",
          nextDayPlan: "",
        });
        loadTaskDetails();
        alert("Daily update added successfully!");
      } else {
        alert(data.message || "Error adding daily update");
      }
    } catch (error) {
      console.error("Error adding daily update:", error);
      if (error.message.includes("403")) {
        alert("Access denied. Please login again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        alert("Error adding daily update");
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleWeeklyUpdate = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("token"); // ✅ FIXED
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/weekly-update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(weeklyUpdate),
        }
      );

      const data = await response.json();
      if (data.success) {
        setShowWeeklyUpdate(false);
        setWeeklyUpdate({
          weeklyProgress: "",
          hoursWorked: "",
          achievements: "",
          blockers: "",
          overallRating: 3,
        });
        loadTaskDetails();
        alert("Weekly update added successfully!");
      } else {
        alert(data.message || "Error adding weekly update");
      }
    } catch (error) {
      console.error("Error adding weekly update:", error);
      alert("Error adding weekly update");
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteTask = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("token"); // ✅ FIXED
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/complete`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(completion),
        }
      );

      const data = await response.json();
      if (data.success) {
        setShowCompletionModal(false);
        onUpdate(data.task);
        alert("Task completed successfully!");
      } else {
        alert(data.message || "Error completing task");
      }
    } catch (error) {
      console.error("Error completing task:", error);
      alert("Error completing task");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !canComment) return;

    try {
      setAddingComment(true);
      const token = localStorage.getItem("token"); // ✅ FIXED
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/tasks/${task._id}/comment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ comment: newComment }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setComments(data.comments);
        setNewComment("");
      } else {
        console.error("Error adding comment:", data.message);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setAddingComment(false);
    }
  };
  const addDeliverable = () => {
    setCompletion({
      ...completion,
      deliverables: [...completion.deliverables, { name: "", description: "" }],
    });
  };

  const removeDeliverable = (index) => {
    setCompletion({
      ...completion,
      deliverables: completion.deliverables.filter((_, i) => i !== index),
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: "#ff4757",
      high: "#ff6b6b",
      medium: "#ffa726",
      low: "#66bb6a",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#64b5f6",
      "in-progress": "#ffb74d",
      completed: "#81c784",
      overdue: "#e57373",
    };
    return colors[status] || colors.pending;
  };

  const isOverdue = () => {
    return new Date() > new Date(task.deadline) && task.status !== "completed";
  };

  return (
    <div className="modal-overlay">
      <div className="task-detail-modal">
        <div className="modal-header">
          <div>
            <h2 className="task-title">{task.title}</h2>
            <div className="task-badges">
              <span
                className="badge priority-badge"
                style={{ backgroundColor: getPriorityColor(task.priority) }}
              >
                {task.priority}
              </span>
              <span
                className="badge status-badge"
                style={{ backgroundColor: getStatusColor(task.status) }}
              >
                {task.status}
              </span>
            </div>
          </div>
          <div className="modal-header-actions">
            {/* Add Delete Button */}
            {canDelete && (
              <button
                className="delete-task-btn"
                onClick={handleDeleteClick}
                title="Delete task"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
            <button className="close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="modal-content">
          {/* Task Description */}
          <div className="section">
            <h3>Description</h3>
            <p className="task-description">{task.description}</p>
          </div>

          {/* Basic Task Info */}
          <div className="info-grid">
            <div className="info-card">
              <div className="info-card-title">Assigned To</div>
              <div className="user-info">
                <i className="fas fa-user"></i>
                <span>{task.assignedTo.name}</span>
                <span className="user-role">({task.assignedTo.role})</span>
              </div>
            </div>
            <div className="info-card">
              <div className="info-card-title">Assigned By</div>
              <div className="user-info">
                <i className="fas fa-user-tie"></i>
                <span>{task.assignedBy.name}</span>
                <span className="user-role">({task.assignedBy.role})</span>
              </div>
            </div>
            <div className="info-card">
              <div className="info-card-title">Deadline</div>
              <div className={`deadline-info ${isOverdue() ? "overdue" : ""}`}>
                <i className="fas fa-calendar"></i>
                <span>{formatDate(task.deadline)}</span>
                {isOverdue() && <div className="overdue-text">OVERDUE</div>}
              </div>
            </div>
            <div className="info-card">
              <div className="info-card-title">Category</div>
              <div className="category-info">
                <i className="fas fa-tag"></i>
                <span>{task.category || "General"}</span>
              </div>
            </div>
          </div>

          {/* Analytics Section - Only for admin/hr */}
          {canViewDetails && taskDetails && (
            <div className="analytics-section">
              <h3>Task Analytics</h3>
              <div className="analytics-grid">
                <div className="analytic-item">
                  <span className="analytic-label">Total Time Spent:</span>
                  <strong className="analytic-value">
                    {Math.round(taskDetails.analytics.totalTimeSpent / 60)}{" "}
                    hours
                  </strong>
                </div>
                <div className="analytic-item">
                  <span className="analytic-label">Daily Updates:</span>
                  <strong className="analytic-value">
                    {taskDetails.analytics.totalDailyUpdates}
                  </strong>
                </div>
                <div className="analytic-item">
                  <span className="analytic-label">Weekly Updates:</span>
                  <strong className="analytic-value">
                    {taskDetails.analytics.totalWeeklyUpdates}
                  </strong>
                </div>
                <div className="analytic-item">
                  <span className="analytic-label">Task Age:</span>
                  <strong className="analytic-value">
                    {taskDetails.analytics.taskAge} days
                  </strong>
                </div>
                <div className="analytic-item">
                  <span className="analytic-label">Has Today's Update:</span>
                  <strong
                    className={`analytic-value ${
                      taskDetails.analytics.hasTodayUpdate ? "success" : "error"
                    }`}
                  >
                    {taskDetails.analytics.hasTodayUpdate ? "Yes" : "No"}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* Latest Updates - Only for admin/hr */}
          {canViewDetails &&
            taskDetails &&
            taskDetails.task.dailyUpdates.length > 0 && (
              <div className="latest-update-section">
                <h3>Latest Daily Update</h3>
                {(() => {
                  const latest =
                    taskDetails.task.dailyUpdates[
                      taskDetails.task.dailyUpdates.length - 1
                    ];
                  return (
                    <div className="latest-update-content">
                      <div className="update-item">
                        <span className="update-label">Date:</span>
                        <strong>{formatDate(latest.date)}</strong>
                      </div>
                      <div className="update-item">
                        <span className="update-label">Work Done:</span>
                        <p className="update-text">{latest.workDone}</p>
                      </div>
                      <div className="update-item">
                        <span className="update-label">Time Spent:</span>
                        <strong>{latest.timeSpent} minutes</strong>
                      </div>
                      {latest.challenges && (
                        <div className="update-item">
                          <span className="update-label">Challenges:</span>
                          <p className="update-text">{latest.challenges}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

          {/* Employee Update Buttons */}
          {canAddUpdates && (
            <div className="update-buttons-container">
              <button
                className="update-btn daily-update-btn"
                onClick={() => setShowDailyUpdate(true)}
              >
                <i className="fas fa-calendar-day"></i>
                Add Daily Update
              </button>
              <button
                className="update-btn weekly-update-btn"
                onClick={() => setShowWeeklyUpdate(true)}
              >
                <i className="fas fa-chart-bar"></i>
                Add Weekly Update
              </button>
              {task.status === "in-progress" && (
                <button
                  className="update-btn complete-btn"
                  onClick={() => setShowCompletionModal(true)}
                >
                  <i className="fas fa-check"></i>
                  Complete Task
                </button>
              )}
            </div>
          )}

          {/* Progress Section */}
          {canUpdateStatus && task.status !== "completed" && (
            <div className="section">
              <h3>Progress</h3>
              <div className="progress-section">
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{progress}%</span>
                </div>

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={(e) => setProgress(parseInt(e.target.value))}
                  className="progress-slider"
                />
              </div>
            </div>
          )}

          {/* Status Change Buttons */}
          {canUpdateStatus && (
            <div className="section">
              <h3>Actions</h3>
              <div className="action-buttons">
                {task.status === "pending" && (
                  <button
                    className="action-btn start-btn"
                    onClick={() => handleStatusChange("in-progress")}
                    disabled={updating}
                  >
                    <i className="fas fa-play"></i>
                    Start Task
                  </button>
                )}
                {task.status === "in-progress" && (
                  <button
                    className="action-btn pause-btn"
                    onClick={() => handleStatusChange("pending")}
                    disabled={updating}
                  >
                    <i className="fas fa-pause"></i>
                    Pause Task
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="section">
            <h3>Comments ({comments.length})</h3>

            {canComment && (
              <div className="add-comment">
                <textarea
                  className="comment-textarea"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows="3"
                />
                <button
                  className="btn btn-primary"
                  onClick={handleAddComment}
                  disabled={addingComment || !newComment.trim()}
                >
                  {addingComment ? "Adding..." : "Add Comment"}
                </button>
              </div>
            )}

            <div className="comments-list">
              {comments.map((comment, index) => (
                <div key={index} className="comment">
                  <div className="comment-header">
                    <strong className="comment-author">
                      {comment.user?.name || "Unknown User"}
                    </strong>
                    <span className="comment-time">
                      {formatDate(comment.timestamp)}
                    </span>
                  </div>
                  <p className="comment-text">{comment.comment}</p>
                </div>
              ))}

              {comments.length === 0 && (
                <div className="no-comments">
                  <i className="fas fa-comments"></i>
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Daily Update Modal */}
      {showDailyUpdate && (
        <div className="update-modal-overlay">
          <div className="update-form">
            <h3>Add Daily Update</h3>

            <div className="form-group">
              <label className="form-label">What did you do today? *</label>
              <textarea
                className="form-textarea"
                value={dailyUpdate.workDone}
                onChange={(e) =>
                  setDailyUpdate({ ...dailyUpdate, workDone: e.target.value })
                }
                placeholder="Describe what you accomplished today..."
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Time Spent (minutes) *</label>
              <input
                className="form-input"
                type="number"
                value={dailyUpdate.timeSpent}
                onChange={(e) =>
                  setDailyUpdate({ ...dailyUpdate, timeSpent: e.target.value })
                }
                placeholder="e.g., 480"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Challenges Faced</label>
              <textarea
                className="form-textarea"
                value={dailyUpdate.challenges}
                onChange={(e) =>
                  setDailyUpdate({ ...dailyUpdate, challenges: e.target.value })
                }
                placeholder="Any challenges or blockers you faced..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Next Day Plan</label>
              <textarea
                className="form-textarea"
                value={dailyUpdate.nextDayPlan}
                onChange={(e) =>
                  setDailyUpdate({
                    ...dailyUpdate,
                    nextDayPlan: e.target.value,
                  })
                }
                placeholder="What you plan to work on tomorrow..."
              />
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDailyUpdate(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleDailyUpdate}
                disabled={
                  updating || !dailyUpdate.workDone || !dailyUpdate.timeSpent
                }
              >
                {updating ? "Adding..." : "Add Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Update Modal */}
      {showWeeklyUpdate && (
        <div className="update-modal-overlay">
          <div className="update-form">
            <h3>Add Weekly Update</h3>

            <div className="form-group">
              <label className="form-label">Weekly Progress *</label>
              <textarea
                className="form-textarea"
                value={weeklyUpdate.weeklyProgress}
                onChange={(e) =>
                  setWeeklyUpdate({
                    ...weeklyUpdate,
                    weeklyProgress: e.target.value,
                  })
                }
                placeholder="Summarize your progress this week..."
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Hours Worked This Week *</label>
              <input
                className="form-input"
                type="number"
                value={weeklyUpdate.hoursWorked}
                onChange={(e) =>
                  setWeeklyUpdate({
                    ...weeklyUpdate,
                    hoursWorked: e.target.value,
                  })
                }
                placeholder="e.g., 40"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Key Achievements</label>
              <textarea
                className="form-textarea"
                value={weeklyUpdate.achievements}
                onChange={(e) =>
                  setWeeklyUpdate({
                    ...weeklyUpdate,
                    achievements: e.target.value,
                  })
                }
                placeholder="What did you achieve this week..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Blockers / Issues</label>
              <textarea
                className="form-textarea"
                value={weeklyUpdate.blockers}
                onChange={(e) =>
                  setWeeklyUpdate({ ...weeklyUpdate, blockers: e.target.value })
                }
                placeholder="Any blockers or issues you encountered..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Overall Rating (1-5)</label>
              <input
                className="form-input"
                type="range"
                min="1"
                max="5"
                value={weeklyUpdate.overallRating}
                onChange={(e) =>
                  setWeeklyUpdate({
                    ...weeklyUpdate,
                    overallRating: e.target.value,
                  })
                }
              />
              <div className="rating-display">
                {weeklyUpdate.overallRating} out of 5
              </div>
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowWeeklyUpdate(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleWeeklyUpdate}
                disabled={
                  updating ||
                  !weeklyUpdate.weeklyProgress ||
                  !weeklyUpdate.hoursWorked
                }
              >
                {updating ? "Adding..." : "Add Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Completion Modal */}
      {showCompletionModal && (
        <div className="update-modal-overlay">
          <div className="update-form completion-form">
            <h3>Complete Task</h3>

            <div className="form-group">
              <label className="form-label">Completion Notes</label>
              <textarea
                className="form-textarea"
                value={completion.completionNotes}
                onChange={(e) =>
                  setCompletion({
                    ...completion,
                    completionNotes: e.target.value,
                  })
                }
                placeholder="Brief notes about task completion..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Final Report</label>
              <textarea
                className="form-textarea"
                value={completion.finalReport}
                onChange={(e) =>
                  setCompletion({ ...completion, finalReport: e.target.value })
                }
                placeholder="Detailed report of completed work..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Quality Rating (1-5)</label>
              <input
                className="form-input"
                type="range"
                min="1"
                max="5"
                value={completion.qualityRating}
                onChange={(e) =>
                  setCompletion({
                    ...completion,
                    qualityRating: e.target.value,
                  })
                }
              />
              <div className="rating-display">
                {completion.qualityRating} out of 5
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Deliverables</label>
              {completion.deliverables.map((deliverable, index) => (
                <div key={index} className="deliverable-item">
                  <input
                    className="form-input"
                    placeholder="Deliverable name"
                    value={deliverable.name}
                    onChange={(e) => {
                      const newDeliverables = [...completion.deliverables];
                      newDeliverables[index].name = e.target.value;
                      setCompletion({
                        ...completion,
                        deliverables: newDeliverables,
                      });
                    }}
                  />
                  <textarea
                    className="form-textarea"
                    placeholder="Deliverable description"
                    value={deliverable.description}
                    onChange={(e) => {
                      const newDeliverables = [...completion.deliverables];
                      newDeliverables[index].description = e.target.value;
                      setCompletion({
                        ...completion,
                        deliverables: newDeliverables,
                      });
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger remove-deliverable-btn"
                    onClick={() => removeDeliverable(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary add-deliverable-btn"
                onClick={addDeliverable}
              >
                + Add Deliverable
              </button>
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCompletionModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCompleteTask}
                disabled={updating}
              >
                {updating ? "Completing..." : "Complete Task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetailModal;
